import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Card, Loader } from '@mantine/core';
import { useTimer } from 'react-timer-hook';
import LogoMap from '../../components/icons/logo';
import CopyButton from '../../components/elements/CopyButton';
import Button from '../../components/elements/Button';
import IconArrowRight from '../../components/icons/IconArrowRight';
import { isAppleDevice } from '../../utils/validation';
import { formatCountdown } from '../../utils/format';
import { fetchQrData } from './actions';
import PropTypes from 'prop-types';
import styles from './styles.scoped.css';

export default function SetupAuthenticator() {
  const dispatch = useDispatch();
  const invalidCode = [403, 401];
  const [ isTimeout, setIsTimeout ] = useState(false);
  const dataQr = useSelector((s) => s.setupAuthenticator);
  const search = typeof window !== 'undefined' && typeof window.location?.search === 'string' ? window.location.search : '';
  const code = search ? new URLSearchParams(search).get('code') : null;
  const dateNow = new Date();

  const { minutes, seconds, restart } = useTimer({
    expiryTimestamp: dateNow,
    onExpire: () => {
      setIsTimeout(true);
    },
    autoStart: false
  });

  useEffect(() => {
    if (code) {
      dispatch(fetchQrData(code));
    } else {
      location.replace('/dc/login');
    }
  }, []);

  useEffect(() => {
    if (dataQr?.data?.countdown) {
      const time = new Date();
      time.setSeconds(time.getSeconds() + dataQr?.data?.countdown);
      restart(time);
    }
  }, [dataQr?.isLoading]);

  return (
    <div className={styles.root}>
      <div>
        <Card className={styles.card}>
          <div className={styles['logo-wrapper']}>
            <LogoMap className={styles['logo-myptm']} />
          </div>
          {isTimeout || invalidCode.includes(dataQr?.errorCode) ? (
            <QrTimeout />
          ) : (
            <QrContent countTime={{ minutes, seconds }} dataQr={dataQr} />
          )}
        </Card>
      </div>
      <div>
        <img
          alt="illustration authenticator"
          src="/assets/illu-authenticator.svg"
        />
      </div>
    </div>
  );
}

function QrContent({ dataQr, countTime }) {
  const urlDownloadApps = isAppleDevice() ? 'https://apps.apple.com/id/app/microsoft-authenticator/id983156458?l=id' : 'https://play.google.com/store/apps/details?id=com.azure.authenticator';
  const { minutes, seconds } = countTime;

  return (
    <>
      <h3>Hubungkan Aplikasi Authenticator</h3>
      <span>
        Untuk keamanan akun Anda, hubungkan akun Anda dengan aplikasi Microsoft Authenticator.
      </span>
      <span className={styles['link-wrapper']}>
        <a href={urlDownloadApps} rel="noopener noreferrer" target="_blank">
          Unduh Aplikasi Authenticator
        </a>
      </span>
      <hr className={styles.separator} />
      <span>
        Pindai kode QR di bawah dengan aplikasi authenticator sebelum:&nbsp;
        <strong>{formatCountdown(minutes, seconds)}</strong>
      </span>
      {dataQr?.data?.uri && !dataQr?.isLoading ? (
        <div className={styles['qr-container']}>
          <QRCode size={200} value={dataQr?.data?.uri} />
          <div className={styles['copy-wrapper']}>
            <span>{dataQr?.data?.secretCode}</span>
            <CopyButton value={dataQr?.data?.secretCode} />
          </div>
          <span>
            Jika Anda tidak dapat memindai kode QR di atas, harap masukkan kode
            di atas ke aplikasi authenticator.
          </span>
        </div>
      ) : (
        <Loader className={styles.loader} color="blue" />
      )}
      <span>
        PIN yang Anda dapat di aplikasi authenticator dapat Anda gunakan untuk
        melanjutkan masuk ke Dashboard Console Subsidi Tepat.
      </span>
    </>
  );
}

QrContent.propTypes = {
  countTime: PropTypes.shape({
    minutes: PropTypes.number,
    seconds: PropTypes.number,
  }),
  dataQr: PropTypes.object,
};

function QrTimeout() {
  const navigate = useNavigate();

  return (
    <>
      <h3>Hubungkan Aplikasi Authenticator</h3>
      <span>
        Anda telah melebihi batas waktu untuk menghubungkan aplikasi
        authenticator (<strong>60 menit</strong>). Silakan masuk kembali ke
        Dashboard Console untuk membuat permintaan baru menghubungkan aplikasi
        authenticator.
      </span>
      <img
        alt="QR Timeout"
        className={styles['timeout-img']}
        src="/assets/illu-qr-timeout.svg"
      />
      <Button
        className={styles['btn-login']}
        onClick={() => navigate('/login')}
        style="primary-500"
      >
        Ke Halaman Masuk
        <IconArrowRight />
      </Button>
    </>
  );
}
